"use client";

import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useFetchAccessPoint } from "../../lib/util";
import { useClient } from "../../lib/context/auth-context";
import { useHistoryManager } from "../history/useHistoryManager";
import {
  setOrganisation,
  setAccessPoint,
  setAccessPointId,
  setAccessPoints,
} from "../slice/accessOrg-slice";
import { useAppDispatch } from "../slice/hooks";

export const useMarketplaceOrgs = (initialUrlData = {}, token, limit = 10) => {
  const client = useClient();
  const dispatch = useAppDispatch();
  const { navigateTo } = useHistoryManager();

  const [state, setState] = useState({
    accessPoints: [],
    limit: limit,
    total: 0,
    currentPage: 1,
    page: 1,
    randomSeed: null,
    search: "",
    ...initialUrlData,
  });

  const [switching, setSwitching] = useState(false);
  const [newAccessPoint, setNewAccessPoint] = useState(null);

  // --------------------------
  //  FETCH ACCESSPOINTS
  // --------------------------
  const accQueryKey = useMemo(
    () => ["feeds", state.limit, state.page, state.search],
    [state.limit, state.page, state.search],
  );

  const {
    data: feeds,
    isLoading,
    isFetching,
    error,
  } = useQuery(
    accQueryKey,
    async () => {
      const params = [
        `limit=${state.limit}`,
        `page=${state.page}`,
        `searchTerm=${state.search}`,
      ];
      if (state.page > 1 && state.randomSeed) {
        params.push(`randomSeed=${state.randomSeed}`);
      }
      const res = await client(
        // `organisation/organisations/storefront/enabled?${params.join("&")}`,
        `crowdcontrol/accesspoints/marketplace?${params.join("&")}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      return res.data;
    },
    {
      enabled: Boolean(token),
      keepPreviousData: true,
      onError: (err) => {
        console.error("Failed to fetch accessPoint:", err);
        setSwitching(false);
      },
    },
  );

  useEffect(() => {
    if (feeds && feeds.accessPoints) {
      const { accessPoints, limit, total, currentPage, randomSeed } = feeds;

      setState((prev) => {
        const all = [...(prev.accessPoints || []), ...accessPoints];
        const unique = all.filter(
          (acc, index, self) =>
            index ===
            self.findIndex((a) => a.accessPointId === acc.accessPointId),
        );
        return {
          ...prev,
          accessPoints: unique,
          limit,
          total,
          currentPage,
          randomSeed: prev.page === 1 ? (randomSeed ?? null) : prev.randomSeed,
        };
      });
    }
  }, [feeds]);

  const openPage = (page) => {
    setState((prev) => ({ ...prev, page }));
  };

  // --------------------------
  //  FETCH ACCESS POINT + SWITCH
  // --------------------------
  const { mutateAsync: fetchAccessPoint } = useFetchAccessPoint({
    onError: (error) => {
      console.error("Failed to fetch access point:", error);
      setSwitching(false);
    },
    onSuccess: (response) => {
      const accessPoints = response.data.accessPoints;
      navigateTo(
        `${`store/${newAccessPoint.orgDetails?.handle}`} `,
        {
          urlData: {
            accessPointId: newAccessPoint.accessPointId,
            organisation: newAccessPoint.orgDetails,
            accessPoints: accessPoints,
            accessPoint: newAccessPoint,
          },
          onNavigate: (navigate) => {
            setSwitching(false);
            if (navigate) {
              if (accessPoints && accessPoints.length > 0) {
                dispatch(setAccessPoints(accessPoints));
              } else {
                console.log("No access points found");
              }
              if (typeof window !== "undefined") {
                window.scrollTo({
                  top: 0,
                  behavior: "smooth",
                });
              }
            }
          },
        },
        false,
      );
    },
  });

  const handleSwitch = async (acc) => {
    setNewAccessPoint(acc);
    dispatch(setOrganisation(acc.orgDetails));
    dispatch(setAccessPointId(acc.accessPointId));
    dispatch(setAccessPoint(acc));
    setSwitching(true);
    fetchAccessPoint({
      orgId: acc.profileId,
      token,
    });
  };

  return {
    state,
    openPage,
    handleSwitch,
    switching,
    isLoading,
    isFetching,
    error,
  };
};
